var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { AddAnnotationIntoCurrentRoomRequestMessage, AddAnnotationIntoCurrentRoomResponseMessage, BroadcastToPeerMessage, cutIntoLines, DeleteModelMessage, EnableModelTransformHandlesAction, InteractionFacet, LinkFileIdToSessionMessage, MeshFacet, CollisionFacet, PrimitiveShape, SendMessagesAfterConfirmationAction, SetSnapToGridParameterAction, ShowTooltipsBehavior, SnapToGridParm, TooltipInfoFacet, TooltipPlacement, TooltipTextAlign, LocalTransformFacet, UpdateFacetOnModelMessage, convertToNumber3, convertToNumber4 } from "flavr-common";
import { Color, Matrix4, Quaternion, Vector3 } from "flavr-math";
import { RequestSTTMessage, STTAnnotationAnswerMessage, STTAnnotationRequestMessage, STTMessage } from "@messages/stt/STTMessages";
import { UpdateCollisionShapeLocalTransformMessage } from "@messages/selection";
import { busService } from "@modules/bus";
import { roomService } from "@modules/room/RoomService";
import { modelPool, useExternalMessages } from "@modules/model";
import { UserMeshSharingService } from "../../ui/services/UserMeshSharingService";
class AnnotationService {
    constructor() {
        this.file = undefined;
        this.annotationWorldTransform = new Matrix4();
        this.annotationLocalTransform = new Matrix4();
        this.annotationLocalPosition = new Vector3();
        this.annotationLocalOrientation = new Quaternion();
        this.annotationLocalScale = new Vector3();
        this.handleAddAnnotationIntoCurrentRoomRequestMessage = (msg, askId) => __awaiter(this, void 0, void 0, function* () {
            var _a;
            if (!askId)
                return;
            let responseUid = -1;
            try {
                const username = window.localStorage.getItem("username");
                const colliderScale = [msg.scale[0] * 2, msg.scale[1] * 2, msg.scale[2] * 2]; // Make collider a little bit bigger so it's easier to target
                // Download Mesh once
                if (this.file === undefined) {
                    const responseSphere = yield fetch("meshes/Sphere.glb");
                    const blob = yield responseSphere.blob();
                    this.file = new File([blob], "Sphere.glb");
                }
                // Reuse it every time
                const response = yield UserMeshSharingService.upload(`/api/v1/model/upload`, this.file);
                const sessionName = window.localStorage.getItem("session");
                if (sessionName !== null)
                    this.connection.send(new LinkFileIdToSessionMessage(response.file_id, sessionName));
                const relativeId = `Annotation_${Date.now()}`;
                const currentRoom = modelPool.getRoomOrThrow(roomService.currentRoomModelUID);
                let currentChildModel = currentRoom.getChild(relativeId);
                if (currentChildModel !== undefined) {
                    console.warn(`AnnotationService: Tried to create a new model ${relativeId}, but it seems that there already is a child with that name.`);
                    responseUid = currentChildModel.getUID();
                    return;
                }
                const messages = [];
                currentChildModel = modelPool.createModel(relativeId, currentRoom, useExternalMessages(messages));
                responseUid = currentChildModel.getUID();
                const storedAvatar = JSON.parse((_a = localStorage.getItem("avatar")) !== null && _a !== void 0 ? _a : "null");
                const avatarColorObject = { Material: 0xff0000 };
                if (storedAvatar !== null) {
                    const lightColor = new Color(storedAvatar.baseColor);
                    avatarColorObject.Material = lightColor.getHex();
                }
                const annotationColorObject = { Material: msg.color };
                messages.push(new UpdateFacetOnModelMessage(currentChildModel.getUID(), new MeshFacet("meshes/Sphere.glb", undefined, undefined, undefined, {
                    colors: annotationColorObject,
                    cloneMaterials: true,
                    showBusyIndicator: false
                })));
                this.annotationWorldTransform.compose(this.annotationLocalPosition.fromArray(msg.position), this.annotationLocalOrientation.fromArray(msg.rotation), this.annotationLocalScale.fromArray(msg.scale));
                this.annotationLocalTransform
                    .copy(currentRoom.getWorldTransform())
                    .invert()
                    .multiply(this.annotationWorldTransform);
                this.annotationLocalTransform.decompose(this.annotationLocalPosition, this.annotationLocalOrientation, this.annotationLocalScale);
                messages.push(new UpdateFacetOnModelMessage(currentChildModel.getUID(), new LocalTransformFacet(convertToNumber3(this.annotationLocalPosition), convertToNumber4(this.annotationLocalOrientation), convertToNumber3(this.annotationLocalScale))));
                messages.push(new UpdateFacetOnModelMessage(currentChildModel.getUID(), new TooltipInfoFacet(`${username !== null && username !== void 0 ? username : "Unknown User"}:\n${msg.capturedText}\n\ndouble-click for deletion`, TooltipTextAlign.Center, undefined, TooltipPlacement.RIGHT_OF_MODEL, avatarColorObject.Material, msg.color, true)));
                const collisionShapeLocalTransform = new Matrix4().makeScale(...colliderScale);
                messages.push(new UpdateFacetOnModelMessage(currentChildModel.getUID(), new CollisionFacet(PrimitiveShape.Sphere, true)), new UpdateCollisionShapeLocalTransformMessage(currentChildModel.getUID(), collisionShapeLocalTransform.toArray()));
                messages.push(new UpdateFacetOnModelMessage(currentChildModel.getUID(), new InteractionFacet({
                    behaviors: [new ShowTooltipsBehavior()],
                    onClick: [new EnableModelTransformHandlesAction(true, 1, false, false), new SetSnapToGridParameterAction(SnapToGridParm.SnapAngle, 0.01)],
                    onDoubleClick: [
                        new SendMessagesAfterConfirmationAction([new DeleteModelMessage(currentChildModel.getUID()), new BroadcastToPeerMessage([new DeleteModelMessage(currentChildModel.getUID())])], `Delete comment by ${username !== null && username !== void 0 ? username : "Unknown User"}?`)
                    ]
                })));
                this.connection.sendMultiple([...messages, new BroadcastToPeerMessage(messages)]);
            }
            catch (error) {
                console.error("[AnnotationService] Failed to create annotation", error);
            }
            finally {
                this.connection.answer(askId, new AddAnnotationIntoCurrentRoomResponseMessage(responseUid));
            }
        });
        this.handleAnnotationSTTRequest = (_msg, askId) => __awaiter(this, void 0, void 0, function* () {
            const answer = yield this.retrieveSTTResult();
            this.connection.answer(askId, new STTAnnotationAnswerMessage(answer));
        });
        this.connection = busService.connection("AnnotationService");
    }
    initialize() {
        this.connection.subscribeForAskRequest(STTAnnotationRequestMessage, this.handleAnnotationSTTRequest);
        this.connection.subscribeForAskRequest(AddAnnotationIntoCurrentRoomRequestMessage, this.handleAddAnnotationIntoCurrentRoomRequestMessage);
    }
    retrieveSTTResult() {
        return __awaiter(this, void 0, void 0, function* () {
            let result = "";
            yield this.connection.askByMessageType(new RequestSTTMessage(false), STTMessage).then((msg) => {
                // Search for most confident solution
                result = this.findBestResult(msg.results);
                // Slicing of text into lines
                result = cutIntoLines(result, 32);
            });
            return result;
        });
    }
    findBestResult(solutions) {
        if (solutions.length === 0)
            return "";
        let maxConfidence = -1000;
        let iMaxConfidence = -10;
        let i = 0;
        for (const textResult of solutions) {
            if (maxConfidence < textResult.confidence) {
                maxConfidence = textResult.confidence;
                iMaxConfidence = i;
            }
            i++;
        }
        return solutions[iMaxConfidence].text;
    }
}
export const annotationService = new AnnotationService();
